import React, { Fragment, useRef, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Text } from '@Sage/hoi-poi-ui';
import { useHorizontalResize } from '@Sage/ssm-web5';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classnames from 'classnames';

import { ConfigActions } from 'actions';
import { RESIZE_LAYOUT } from 'constants/Constants';
import { checkIsTrialExpired } from 'utils/fm';

import './styles.scss';

const propTypes = {
    header: PropTypes.node,
    navigator: PropTypes.node,
    navigatorTitle: PropTypes.string,
    children: PropTypes.node,
    className: PropTypes.string,
    withUsageLimit: PropTypes.node,
    resizable: PropTypes.bool,
    resizeStorageKey: PropTypes.string,
};

const { DEFAULT_WIDTH, MIN_WIDTH, MIN_PERCENTAGE, MAX_PERCENTAGE, WIDTH_KEY } =
    RESIZE_LAYOUT.NAVIGATOR;

function mapStateToProps(state, ownProps) {
    const resizeStorageKey = ownProps.resizeStorageKey || WIDTH_KEY;
    return {
        subscriptionModeCode: state.config.subscriptionModeCode,
        cachedWidth: state.config.components?.[resizeStorageKey],
    };
}

function mapDispatchToProps(dispatch) {
    return {
        setConfigWeb: bindActionCreators(ConfigActions, dispatch).setConfigWeb,
    };
}

const DetailLayout = React.memo(
    ({
        header,
        navigator,
        navigatorTitle,
        children,
        subscriptionModeCode,
        className,
        withUsageLimit,
        resizable = false,
        resizeStorageKey = WIDTH_KEY,
        cachedWidth,
        setConfigWeb,
    }) => {
        const containerRef = useRef(null);
        const navigatorRef = useRef(null);

        const [navigatorWidth, setNavigatorWidth] = useState(() => {
            if (!resizable) return null;
            const stored = cachedWidth || localStorage.getItem(resizeStorageKey);
            return stored || `${DEFAULT_WIDTH}px`;
        });

        const handleResize = useCallback(({ percentageList }) => {
            setNavigatorWidth(`${percentageList}%`);
        }, []);

        const handleResizeEnd = useCallback(
            ({ percentageList }) => {
                const newWidth = `${percentageList}%`;
                localStorage.setItem(resizeStorageKey, newWidth);
                setConfigWeb(resizeStorageKey, newWidth);
                setNavigatorWidth(newWidth);
            },
            [resizeStorageKey, setConfigWeb],
        );

        useHorizontalResize({
            targetRef: navigatorRef,
            containerRef,
            minWidth: MIN_WIDTH,
            minPercentage: MIN_PERCENTAGE,
            maxPercentage: MAX_PERCENTAGE,
            onResize: handleResize,
            onResizeEnd: handleResizeEnd,
            enabled: resizable,
        });

        const contentClass = classnames(
            'fm-content-layout',
            {
                'fm-content-layout__free-trial':
                    subscriptionModeCode === 'free_trial' && !checkIsTrialExpired(),
            },
            className,
        );

        const navigatorStyle = useMemo(() => {
            if (!resizable || !navigatorWidth) return undefined;
            return { width: navigatorWidth };
        }, [resizable, navigatorWidth]);

        const navigatorClass = classnames('fm-content-navigator-layout', {
            'fm-content-navigator-layout--resizable': resizable,
        });

        return (
            <Fragment>
                <div className="fm-content-header-layout">{header}</div>
                {withUsageLimit}
                <div ref={containerRef} className={contentClass}>
                    {navigator && (
                        <div ref={navigatorRef} className={navigatorClass} style={navigatorStyle}>
                            {navigatorTitle && (
                                <div className="fm-content-navigator-header">
                                    <Text type="subtitle" medium>
                                        {navigatorTitle}
                                    </Text>
                                </div>
                            )}
                            {navigator}
                        </div>
                    )}
                    <div className="fm-content-content-layout">{children}</div>
                </div>
            </Fragment>
        );
    },
);

DetailLayout.propTypes = propTypes;

export default connect(mapStateToProps, mapDispatchToProps)(DetailLayout);
